/**
 * useCompetitionData - Data Fetching and Computed Values
 *
 * Single responsibility: Fetching and transforming competition data.
 * Handles all data-related logic for CompetitionView.
 */

import { useMemo } from 'react';
import { useCompetitions } from '@/hooks/useCompetitions';
import { usePickemById } from '@/api/query/usePickem';
import { usePickemSelections } from '@/hooks/usePickemSelections';
import { useSubmitSelections } from '@/api/mutations/useSubmitSelections';
import { formatTimeRemaining, formatRelativeTime } from '@/lib/dateFormat';
import { toQuestionId } from '@/utils/type-guards';
import { isPickemEntryClosed } from '@/utils/pickem-entry';
import { mapPickemStatusToGameState, transformQuestions } from '@/utils/type-transformers';

interface UseCompetitionDataProps {
  gameId: string;
  onSuccessSubmit: () => void;
}

/**
 * Hook for managing competition data and computed values
 *
 * @param gameId - The competition ID
 * @param onSuccessSubmit - Callback when submission succeeds
 * @returns All data, computed values, and selection handlers
 */
export function useCompetitionData({ gameId, onSuccessSubmit }: UseCompetitionDataProps) {
  // Data fetching
  const { getCompetition } = useCompetitions();
  const competition = getCompetition(gameId);
  const { data: pickemDetails, isLoading, error } = usePickemById(gameId);

  // Selection management
  const {
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    allQuestionsAnswered,
    buildSubmissionPayload,
  } = usePickemSelections(pickemDetails);

  // Submission
  const hasExistingSelections = (pickemDetails?.pickem.selections?.length ?? 0) > 0;
  const submitMutation = useSubmitSelections({
    pickemId: gameId,
    isUpdate: hasExistingSelections,
    showToast: false,
    onSuccess: onSuccessSubmit,
  });

  // Computed values
  const selectionResults = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const results: Record<string, boolean> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      if (selection.selection_is_correct !== undefined) {
        results[toQuestionId(selection.question_id)] = selection.selection_is_correct;
      }
    });

    return results;
  }, [pickemDetails]);

  const selectionPoints = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const points: Record<string, number> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      points[toQuestionId(selection.question_id)] = selection.points || 0;
    });

    return points;
  }, [pickemDetails]);

  const timeDisplay = useMemo(() => {
    const comp =
      competition ||
      (pickemDetails
        ? {
            state: mapPickemStatusToGameState(
              pickemDetails.pickem.pickem_status,
              new Date(pickemDetails.pickem.start_utc || new Date().toISOString())
            ),
            gameDate: pickemDetails.pickem.start_utc || new Date().toISOString(),
          }
        : null);

    if (!comp) return '';

    const gameDate = typeof comp.gameDate === 'string' ? new Date(comp.gameDate) : comp.gameDate;

    if (!(gameDate instanceof Date)) {
      return '';
    }

    switch (comp.state) {
      case 'countdown':
        return formatTimeRemaining(gameDate);
      case 'live':
        return 'Live';
      case 'final':
        return formatRelativeTime(gameDate);
      default:
        return formatRelativeTime(gameDate);
    }
  }, [competition, pickemDetails]);

  const competitionData = useMemo(() => {
    if (competition) return competition;

    // Fallback if competition not in context
    const gameDate = new Date(pickemDetails?.pickem.start_utc || new Date().toISOString());

    return {
      id: gameId,
      state: mapPickemStatusToGameState(pickemDetails?.pickem.pickem_status, gameDate),
      gameDate,
      title: pickemDetails?.pickem.title_en || 'Competition',
      finalScore:
        pickemDetails?.pickem.pickem_status === 'COMPLETE' &&
        typeof pickemDetails?.pickem.user_ranking === 'number' &&
        typeof pickemDetails?.pickem.total_points === 'number'
          ? { rank: pickemDetails.pickem.user_ranking, points: pickemDetails.pickem.total_points }
          : undefined,
      // Fallback path (competition not found in the list context) — source
      // reserve-a-seat fields directly off the detail payload so the
      // Reserve Entry screen branch below works even here (DKPLAT-528).
      hasReservation: pickemDetails?.pickem.has_reservation ?? false,
      // Real "has questions yet" signal — derived from the playable questions
      // array, not questionsPostedAt (see type-transformers.ts).
      hasQuestions: transformQuestions(pickemDetails?.pickem.questions ?? []).length > 0,
      questionsPostedAt: pickemDetails?.pickem.questions_posted_at ?? null,
      reserveASeatEnabled: pickemDetails?.pickem.reserve_a_seat_enabled ?? false,
    };
  }, [competition, pickemDetails, gameId]);

  // Derived state — lock entry/edits once contest start (or earliest fixture) passes,
  // even if status transition (OPEN → INPLAY) has not run yet.
  const isContestClosed = isPickemEntryClosed(pickemDetails?.pickem);
  const isViewOnly =
    isContestClosed || competitionData.state === 'live' || competitionData.state === 'final';
  const isResultsMode = competitionData.state === 'live' || competitionData.state === 'final';
  const submitButtonText = hasExistingSelections ? 'Update Selections' : 'Save Changes';

  return {
    // Data
    pickemDetails,
    competitionData,
    isLoading,
    error,

    // Selections
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    allQuestionsAnswered,
    buildSubmissionPayload,

    // Submission
    submitMutation,
    hasExistingSelections,
    submitButtonText,

    // Computed values
    selectionResults,
    selectionPoints,
    timeDisplay,

    // Derived state
    isContestClosed,
    isViewOnly,
    isResultsMode,
  };
}
